export type ServerError = { code: string; description: string };

function recordOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function itemsOf(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;

  const record = recordOf(payload);

  return record !== null && Array.isArray(record.errors) ? record.errors : [];
}

const apiActionErrorCodes: Record<number, string> = {
  900: "InvalidCredentials",
  901: "EmailNotConfirmed"
};

function textOf(record: Record<string, unknown>, camel: string, pascal: string): string {
  const value = typeof record[camel] === "string" ? record[camel] : record[pascal];

  return typeof value === "string" ? value : "";
}

function apiActionErrorOf(payload: unknown): ServerError | null {
  const record = recordOf(payload);

  if (record === null || typeof record.errorCode !== "number") return null;

  const code = apiActionErrorCodes[record.errorCode];

  return code === undefined ? null : { code, description: textOf(record, "message", "Message") };
}

export function serverErrorsOf(payload: unknown): ServerError[] {
  const apiActionError = apiActionErrorOf(payload);

  if (apiActionError !== null) return [apiActionError];

  const errors: ServerError[] = [];

  for (const item of itemsOf(payload)) {
    const record = recordOf(item);

    if (record === null) continue;

    const code = textOf(record, "code", "Code");

    if (code.length === 0) continue;

    errors.push({ code, description: textOf(record, "description", "Description") });
  }

  return errors;
}

export class ForteXApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.name = "ForteXApiError";
    this.status = status;
    this.payload = payload;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }

  get codes(): string[] {
    return serverErrorsOf(this.payload).map(a => a.code);
  }
}

export function messageOf(status: number, payload: unknown): string {
  if (typeof payload === "string" && payload.trim().length > 0) return payload;

  const described = itemsOf(payload)
    .map(item => (typeof item === "string" ? item : recordOf(item)?.description ?? recordOf(item)?.Description))
    .filter((a): a is string => typeof a === "string" && a.length > 0);

  if (described.length > 0) return described.join(" ");

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const candidates = [record.message, record.Message, record.error, record.detail, record.title];
    const found = candidates.find(a => typeof a === "string" && a.length > 0);

    if (typeof found === "string") return found;

    if (Array.isArray(record.problems) && record.problems.length > 0) return record.problems.map(String).join("; ");
  }

  return status === 0 ? "network" : `HTTP ${status}`;
}

export function isAbortError(error: unknown): boolean {
  return (error instanceof DOMException || error instanceof Error) && (error.name === "AbortError" || error.name === "TimeoutError");
}
